import axios from 'axios';
import { useQuery } from 'react-query';
import { useMemo } from 'react';
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import userType from '../../../constants/userType.js';
import { masterLogout, reset } from '../../../features/masterAuth/masterAuthSlice.js';
import { reset as invoiceReset } from "../../../features/invoice/invoiceSlice.js";
import { reset as headerReset } from "../../../features/headerText/headerTextSlice.js";
import { reset as appSettingsReset } from "../../../features/appSettings/appSettingsSlice.js";

export const fetchMasterUserProfiles = async (token, dispatch, navigate) => {
    if (!token) {
        throw new Error("Token is missing");
    }

    try {
        const response = await axios.get('/api/master/user', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    } catch (error) {
        console.log("Master users Error:", error);
        global.config.slackMessage(error.toString());
        
        if (error.response?.data?.status === "deleted") {
            localStorage.removeItem("user");
            dispatch(masterLogout());
            dispatch(reset());
            dispatch(invoiceReset());
            dispatch(headerReset());
            dispatch(appSettingsReset());
            toast.error('This user is deleted!', {
                position: "bottom-right",
                autoClose: 1500,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
            });
            navigate("/");
            return null;
        } else if (error.response?.status === 403) {
            navigate("../../../404");
            return null;
        } else if (error.response?.status === 401) {
            navigate("/master-user/login");
            return null;
        } else {
            console.log(error);
            navigate("../../../404");
            return null;
        }
    }
};

export const useMasterUserProfiles = (token) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const queryKey = useMemo(() => ["masterUsersProfiles", token], [token]);

    const fetchFunction = useMemo(
        () => () => fetchMasterUserProfiles(token, dispatch, navigate),
        [token, dispatch, navigate]
    );

    return useQuery(queryKey, fetchFunction, {
        enabled: !!token,
        staleTime: 1000 * 60 * 5,
        cacheTime: 1000 * 60 * 10,
        refetchOnWindowFocus: false,
        retry: 2,
        refetchOnMount: false,
        keepPreviousData: true,
    });
};

export const useMasterUserType = (token) => {
    const queryKey = useMemo(() => ["masterUserType", token], [token]);

    const fetchFunction = useMemo(
        () => async () => {
            if (!token) {
                throw new Error("Token is missing");
            }
            const response = await axios.get(`/api/master/user-type`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            const filteredUserType = response.data.filter(
                (role) =>
                    role._id !== userType.Collaborator &&
                    role._id !== userType.Business
            );
            return filteredUserType;
        },
        [token]
    );

    return useQuery(queryKey, fetchFunction, {
        enabled: !!token,
        staleTime: 1000 * 60 * 5,
        cacheTime: 1000 * 60 * 10,
        refetchOnWindowFocus: false,
        retry: 2,
        refetchOnMount: false,
        keepPreviousData: true,
    });
};